import React, {useState} from 'react';
import ArticleView from 'epic-ue-blog/app/scripts/views/ArticleView';
import {
    getBasicMetaTags,
    RouteMatchOrUrlParse,
    STARDUST_ARTICLE_TEMPLATE_NAME
} from 'epic-ue-shared';
import {getAltLocPageDataCards, useFeedStoreItem, useLocalizedTags} from 'epic-ue-feed';
import {
    AppSharedKeys,
    InitSharedValueResponse,
    useCmsData,
    cmsApi,
    SPOTLIGHT_TEMPLATE_NAME
} from 'epic-ue-shared';
import ErrorPage from '../pages/ErrorPage';
import ErrorLocPage from '../pages/ErrorLocPage';
import {PathMatch, useLocation, useParams, Params} from 'react-router-dom';
import {useLocalization, extractSlugLocale} from '@epic-mw/localization';
import {UnrealLoading} from 'epic-ue-loading';
import {getLocale} from '@epic-core/common';
import {getNoTrailingSlash} from '@epic-mw/common-utils';
import {SpotlightPage} from 'epic-ue-spotlight';
import {feedPreloadSharedValues} from './FeedDetailsPage';
import {getMessageFnType} from 'epic-ue-shared';
import ArticleDetailStardust from '../components/article/ArticleDetailStardust';
import {ClassicRouteChildren} from '../classic/ClassicRoute';

const getSlug = (pathname: string, params: Params, props = {} as Properties) => {
    const blog = props.blog || {};
    const slug = blog._slug || params.title || params.typeSlug; // foo
    let pageSlug;
    let rootPageSlug;

    if (pathname) {
        const extracted = extractSlugLocale(pathname, getLocale()); // blog/foo
        pageSlug = getNoTrailingSlash(extracted.pageSlug);
        rootPageSlug = pageSlug.replace(`/${slug}`, ''); // blog
        return {slug, pageSlug, rootPageSlug};
    } else {
        rootPageSlug = props.rootPageSlug; // blog
        pageSlug = getNoTrailingSlash(`${rootPageSlug}/${slug}`); // blog/foo
    }

    return {slug, pageSlug, rootPageSlug};
};

interface Properties {
    rootPageSlug?: string;
    blog?: any;
    linkNavigatorRootPageSlug?: boolean;
    match?: PathMatch | null;
}

const BlogPage = (props: Properties): JSX.Element => {
    const {pathname} = useLocation();
    const params = useParams();
    const getMessage = useLocalization();
    const blog = props && props.blog;
    const {slug, pageSlug, rootPageSlug} = getSlug(pathname, params, props);
    const {
        feedItem,
        cmsLoading: feedItemLoading,
        cmsLoadedEmpty: feedItemLoadedEmpty
    } = useFeedStoreItem(!blog ? pageSlug : '');

    const pageData = blog || feedItem;
    const isSpotlight = pageData && pageData._templateName === SPOTLIGHT_TEMPLATE_NAME;
    const isStardust = pageData && pageData._templateName === STARDUST_ARTICLE_TEMPLATE_NAME;
    const {pageData: rootPageData} = useCmsData<any>(isSpotlight ? '' : rootPageSlug);

    const localizedTags = useLocalizedTags();

    const [articleViewLoading, setArticleViewLoading] = useState(true);

    if (feedItemLoading) {
        return <UnrealLoading />;
    }

    if (feedItemLoadedEmpty) {
        return <ErrorPage status={404} />;
    }

    // if alternate locale page data exists, render alternate 404 page
    if (feedItem?._hasAltLocPageData) {
        const altLocPageCards = getAltLocPageDataCards(
            feedItem,
            pageSlug,
            getMessage as getMessageFnType
        );
        return <ErrorLocPage altLocPageCards={altLocPageCards} />;
    }

    if (pageData && isStardust) {
        return <ArticleDetailStardust pageData={pageData} locale={getLocale()} />;
    }

    if (pageData && isSpotlight) {
        return (
            <ClassicRouteChildren>
                <SpotlightPage
                    pageData={pageData}
                    parentOrigin={'feed'}
                    cmsLoadedEmpty={feedItemLoadedEmpty}
                    cmsLoading={feedItemLoading}
                    urlPattern={pageSlug}
                />
            </ClassicRouteChildren>
        );
    }

    const safeRootPageData = rootPageData || {};
    const config = safeRootPageData.config || {};
    const metaTags = getBasicMetaTags(getMessage as getMessageFnType);

    return (
        <ClassicRouteChildren>
            <div className="blog-wrapper">
                <ArticleView
                    {...props}
                    blog={pageData}
                    slug={slug}
                    overrideMetaTags={metaTags}
                    config={config}
                    localizedTags={localizedTags}
                    cmsLoading={feedItemLoading}
                    setArticleViewLoading={setArticleViewLoading}
                />
            </div>
        </ClassicRouteChildren>
    );
};

BlogPage.getInitialSharedValues = async (
    match: RouteMatchOrUrlParse
): Promise<Array<InitSharedValueResponse>> => {
    const pathMatch = match as PathMatch;
    const {pageSlug, rootPageSlug} = getSlug(pathMatch.pathname, pathMatch.params);

    const responses = await Promise.all([
        cmsApi.getPageData(rootPageSlug),
        cmsApi.getPageData(pageSlug)
    ]);
    delete responses[1].pageSlugParam;

    const valid = !!(pageSlug && responses[1] && Object.keys(responses[1]).length !== 0);
    let altLocPageData;
    if (!valid) {
        altLocPageData = await cmsApi.getAltLocPageData(pageSlug);
    }

    const preloadArray = [
        {
            key: AppSharedKeys.CmsStore,
            value: {
                [rootPageSlug]: responses[0] || {}
            }
        },
        {
            key: AppSharedKeys.CmsStore,
            value: {
                [pageSlug]: altLocPageData || responses[1] || {}
            }
        }
    ];

    const pageData = responses[1] || {};

    if (pageData && pageData._templateName === SPOTLIGHT_TEMPLATE_NAME) {
        return feedPreloadSharedValues(pageSlug, pageData, preloadArray);
    }

    return preloadArray;
};

export default BlogPage;
